import type { Language } from '@/i18n/translations';
import { SITE_ORIGIN } from './siteOrigin';

export const supportedLanguages: readonly Language[] = ['fr', 'nl', 'en'];

export interface LanguageLocation {
  language: Language;
  pathname: string;
  basename: string;
}

export function isSupportedLanguage(value: string | null | undefined): value is Language {
  return supportedLanguages.includes(value as Language);
}

export function parseLanguagePath(pathname: string): LanguageLocation {
  const normalized = normalizePathname(pathname);
  const match = normalized.match(/^\/(fr|nl|en)(?=\/|$)(.*)$/i);

  if (!match) {
    return { language: 'fr', pathname: normalized, basename: '' };
  }

  const language = match[1].toLowerCase() as Language;
  const pathnameWithoutPrefix = normalizePathname(match[2] || '/');

  if (language === 'fr') {
    return { language, pathname: pathnameWithoutPrefix, basename: '' };
  }

  return {
    language,
    pathname: pathnameWithoutPrefix,
    basename: `/${language}`,
  };
}

export function localizePathname(pathname: string, language: Language): string {
  const canonicalPath = parseLanguagePath(pathname).pathname;
  if (language === 'fr') {
    return canonicalPath;
  }
  return canonicalPath === '/' ? `/${language}` : `/${language}${canonicalPath}`;
}

export function localizeInternalUrl(value: string, language: Language): string {
  if (!value || value.startsWith('#') || !value.startsWith('/') || value.startsWith('//')) {
    return value;
  }

  const url = new URL(value, SITE_ORIGIN);
  if (isTechnicalResourcePath(url.pathname)) {
    return value;
  }
  return `${localizePathname(url.pathname, language)}${url.search}${url.hash}`;
}

export function isTechnicalResourcePath(pathname: string): boolean {
  return /^\/(?:api(?:\/|$)|assets\/|images\/|videos\/|favicon(?:\.ico|\.png)$|og-image\.png$|placeholder\.svg$|robots\.txt$|sitemap\.xml$|llms\.txt$|[^/]+\.(?:pdf|png|jpe?g|gif|webp|avif|svg|css|js|map|json|xml|txt|ico|woff2?|ttf|mp4)$)/i.test(pathname);
}

export function canonicalizeLanguageLocation(
  pathname: string,
  search: string,
  hash: string,
): { language: Language; url: string; basename: string } {
  const parsed = parseLanguagePath(pathname);
  const params = new URLSearchParams(search);
  const queryLanguage = params.get('lang');
  const hasExplicitPrefix = /^\/(fr|nl|en)(?=\/|$)/i.test(normalizePathname(pathname));
  const language = hasExplicitPrefix || !isSupportedLanguage(queryLanguage)
    ? parsed.language
    : queryLanguage;

  params.delete('lang');
  const query = params.toString();
  const canonicalPathname = localizePathname(parsed.pathname, language);

  return {
    language,
    basename: language === 'fr' ? '' : `/${language}`,
    url: `${canonicalPathname}${query ? `?${query}` : ''}${hash}`,
  };
}

export function normalizePathname(pathname: string): string {
  const withLeadingSlash = pathname.startsWith('/') ? pathname : `/${pathname}`;
  const collapsed = withLeadingSlash.replace(/\/{2,}/g, '/');
  return collapsed.length > 1 ? collapsed.replace(/\/+$/g, '') : '/';
}
